import { FieldOptionBase } from 'module/api/field/types';
import { ClassNameOnlyProps } from 'module/types/common';
import { SingleUserField } from 'module/types/filter-field/field/filterable-field';
import { VirtualizedMenuList } from 'module/ui/components/atoms/select/virtualized-menu-list';
import { FC, ReactNode, RefObject, useCallback, useMemo } from 'react';
import StateManager from 'react-select';
import styled from 'styled-components';
import { Popover, Select } from '../components/components';
import { UserFieldOptionsGetter } from '../types';

type Props = ClassNameOnlyProps & {
  field: SingleUserField;
  title: ReactNode;
  optionsGetter: UserFieldOptionsGetter;
  onChange: (latest: number | null) => void;
  selectRef: RefObject<StateManager>;
};

const StyledPopover = styled(Popover)`
  height: 400px;
`;

export const ControlledSingleUserFieldEditorPopover: FC<
  React.PropsWithChildren<Props>
> = ({ field, title, onChange, optionsGetter, selectRef, className }) => {
  const options = useMemo(() => optionsGetter(field), [field, optionsGetter]);

  const onValueChange = useCallback(
    (latest: FieldOptionBase) =>
      onChange(field.value === latest?.value ? null : (latest?.value ?? null)),
    [onChange, field],
  );

  const usersInOptions = useMemo(
    () => options.flatMap((option) => option.options),
    [options],
  );

  const selectedUser = useMemo(
    () => usersInOptions.find((u) => u.value === field.value),
    [field.value, usersInOptions],
  );

  return (
    <StyledPopover className={className}>
      <Popover.Title>{title}</Popover.Title>
      <Select
        ref={selectRef}
        type="user"
        grouped
        components={{ MenuList: VirtualizedMenuList }}
        options={options}
        value={selectedUser}
        onChange={onValueChange}
      />
    </StyledPopover>
  );
};
